import BasicButton from "components/General/BasicButton";
import { useNavigate } from "react-router-dom";

import { landTypes } from "utils/game-information-constants";
import InfoPopout from "components/Wrappers/InfoPopout";
import { LandToolTips } from "utils/ToolTipConst";

import { HIGHLIGHTS } from "utils/ToolTipConst";
import PopText from "components/Wrappers/PopText";

const standardClassname = "space-y-2 text-sm p4";
const actionTutorialContent: { [key: string]: React.ReactNode } = {
  "Reed Harvester": (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">
        Reed Harvester Tutorial
      </p>
      <p>
        As a{" "}
        <PopText
          recordName="Reed Harvester"
          text="Reed Harvester"
          recordType="role"
        />
        , your livelihood depends on cutting reeds in the wetlands for{" "}
        <PopText recordName="money" text="money" recordType="generic" />.
      </p>
      <p>
        Your objective is to{" "}
        <span className={HIGHLIGHTS.positive}>maximize Reedbed land</span>
        and harvest it efficiently. A well-managed wetland can be{" "}
        <span className={HIGHLIGHTS.positive}>extremely profitable</span>, but
        you face{" "}
        <span className={HIGHLIGHTS.negative}>
          significant ongoing expenses
        </span>
        .
      </p>
      <p>
        Use the{" "}
        <PopText recordName="cut" text="Cutting Tool" recordType="action" />
        during the winter to harvest reeds. Remember that reeds{" "}
        <span className={HIGHLIGHTS.negative}>cannot be harvested</span>
        if the water level is above 20 cm in winter. Likewise, a high water
        level in spring will{" "}
        <span className={HIGHLIGHTS.negative}>increase your profits</span>!
      </p>
      <p>
        Overharvesting can cause{" "}
        <span className={HIGHLIGHTS.negative}>decay</span> of
        <PopText recordName="reedbed" text="Reedbed" recordType="land" /> into
        <PopText recordName="reed water" text="Reed Water" recordType="land" />
        or even{" "}
        <PopText recordName="open water" text="Open Water" recordType="land" />.
      </p>
      <p>
        Coordinate with other players at the
        <PopText
          recordName="townhall"
          text="Townhall"
          recordType="generic"
        />{" "}
        to influence the{" "}
        <PopText
          recordName="water regime"
          text="Water Management Regime"
          recordType="generic"
        />
        so water levels stay low enough for harvesting.
      </p>

      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Cutting reedbeds three years in a row can{" "}
        <span className={HIGHLIGHTS.negative}>force them to decay</span> into
        reed water.
      </p>
      <p>
        • Reedbeds provide{" "}
        <span className={HIGHLIGHTS.positive}>higher profits</span> than reed
        water when cut, and are more sustainable.
      </p>
      <p>
        • Plan with the{" "}
        <PopText
          recordName="Conservationist"
          text="Conservationist"
          recordType="role"
        />{" "}
        to avoid ecological backlash.
      </p>
    </div>
  ),

  Stockbreeder: (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">
        Stockbreeder Tutorial
      </p>
      <p>
        The{" "}
        <PopText
          recordName="Stockbreeder"
          text="Stockbreeder"
          recordType="role"
        />
        raises cattle for profit. To succeed, you must graze your herd on
        high-quality land and control your upkeep costs.
      </p>
      <p>
        Your goal is to{" "}
        <span className={HIGHLIGHTS.positive}>maximize grazable land</span>,
        especially{" "}
        <PopText recordName="meadow" text="Meadow" recordType="land" />. Good
        grazing land keeps overhead low and herd size high.
      </p>
      <p>
        Use the{" "}
        <PopText recordName="grazing" text="Grazing Tool" recordType="action" />
        to assign{" "}
        <PopText recordName="cell" text="cells" recordType="generic" /> for
        grazing. Each cow requires one unit of food per season.
        <span className={HIGHLIGHTS.negative}>
          If a cow is not fed, you must pay
        </span>
        {" "}to purchase feed.
      </p>
      <p>
        Watch water levels. If water is above 20 cm in a season,
        <span className={HIGHLIGHTS.negative}>cattle cannot graze</span>.
      </p>

      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Meadows give the{" "}
        <span className={HIGHLIGHTS.positive}>best grazing values</span> for
        your herd.
      </p>
      <p>
        • Grazing reedbeds will{" "}
        <span className={HIGHLIGHTS.negative}>destroy them</span> and convert
        them to meadows. This will anger Reed Harvesters and Conservationists,
        so it's best to be conservative in this.
      </p>
      <p>
        • Reinvest profits to grow your herd. Eventually you will outproduce the
        need for grazing.
      </p>
    </div>
  ),

  "Conservationist": (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">
        Conservationist Tutorial
      </p>
      <p>
        As the{" "}
        <PopText
          recordName="Conservationist"
          text="Conservationist"
          recordType="role"
        />
        , your primary aim is to preserve the ecology of the wetlands and
        <span className={HIGHLIGHTS.positive}>maintain bird populations</span>.
      </p>
      <p>
        You are the only role that can see
        <span className={HIGHLIGHTS.neutral}>
          both estates’ halves of the map
        </span>
        . Use this advantage to monitor habitats and influence other players.
      </p>
      <p>
        Your income is limited, so you must{" "}
        <span className={HIGHLIGHTS.neutral}>negotiate and broker deals</span>
        to achieve your goals. Work with other players to create
        <PopText
          recordName="create-nature-reserve"
          text="Nature Reserves"
          recordType="action"
        />
        to protect key habitats.
      </p>
      <p>
        Use persuasion at the
        <PopText recordName="townhall" text="Townhall" recordType="generic" />
        to vote for{" "}
        <PopText
          recordName="water regime"
          text="Water Management Regimes"
          recordType="generic"
        />
        that maintain{" "}
        <span className={HIGHLIGHTS.positive}>healthy wetlands</span>. Create
        and negotiate for nature reserves to protect endangered species.
      </p>
      <p>On the map, icons representing birds will be visible. This shows where that species of bird was present and the end of the previous year.</p>

      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Nature reserves have{" "}
        <span className={HIGHLIGHTS.neutral}>no programmed effects</span>, so
        you must negotiate rules yourself.
      </p>
      <p>
        • Players often assume reserves have automatic effects—use this{" "}
        <span className={HIGHLIGHTS.positive}>to your advantage</span>.
      </p>
      <p>
        • Solicit funding from the private estate; they often have{" "}
        <span className={HIGHLIGHTS.positive}>more disposable income</span>.
      </p>
      <p>
        • Use your full-map vision to expose or reveal hidden actions of others.
      </p>
    </div>
  ),

  "public Hunter": (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">
        Public Hunter Tutorial
      </p>
      <p>
        The{" "}
        <PopText
          recordName="Public Hunter"
          text="Public Hunter"
          recordType="role"
        />
        hunts ducks for income. Your activities are subsidized by the government
        and have <span className={HIGHLIGHTS.neutral}>bracketed income</span>
        limits, but you face{" "}
        <span className={HIGHLIGHTS.positive}>no overhead costs</span>.
      </p>
      <p>
        Ducks thrive in areas with moderate water. Encourage the{" "}
        <PopText recordName="townhall" text="Townhall" recordType="generic" />
        to vote for{" "}
        <PopText
          recordName="water regime"
          text="Water Management Regimes"
          recordType="generic"
        />
        that sustain healthy duck populations.
      </p>
      <p>
        Use the{" "}
        <PopText
          recordName="private-hunt"
          text="Hunting Tool"
          recordType="action"
        />
        to designate{" "}
        <PopText recordName="cell" text="cells" recordType="generic" /> for
        hunting.
      </p>

      <p>On the map cells there are icons representing where the ducks were at the end of the previous year. In each cell is a series of pluses for each water management regime, which represents how many ducks will be in the cell if a particular regime is chosen <PopText recordName="townhall" text="at townhall" recordType="generic" />.</p>

      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Ducks prefer{" "}
        <span className={HIGHLIGHTS.neutral}>moderately wet biomes</span> for
        abundance.
      </p>
      <p>• Build alliances to create meadows or open water out of reedbeds.</p>

      <p>
        • Use government subsidies to bargain with other players for support at
        the{" "}
        <PopText recordName="townhall" text="Townhall" recordType="generic" />.
      </p>
    </div>
  ),

  "private Hunter": (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">
        Private Hunter Tutorial
      </p>
      <p>
        The{" "}
        <PopText
          recordName="Private Hunter"
          text="Private Hunter"
          recordType="role"
        />
        earns income by hunting ducks. The more ducks you hunt, the more{" "}
        <PopText recordName="money" text="money" recordType="generic" /> you
        make.
      </p>
      <p>
        Use the{" "}
        <PopText
          recordName="public-hunt"
          text="Hunting Tool"
          recordType="action"
        />
        to designate{" "}
        <PopText recordName="cell" text="cells" recordType="generic" /> for
        hunting.
      </p>
      <p>
        Maximize duck populations by advocating for
        <PopText
          recordName="water regime"
          text="Water Management Regimes"
          recordType="generic"
        />
        that provide{" "}
        <span className={HIGHLIGHTS.neutral}>moderate flooding</span>.
      </p>
      <p>
        Use the{" "}
        <PopText
          recordName="private-hunt"
          text="Hunting Tool"
          recordType="action"
        />
        to hunt ducks. Your secluded private estate provides
        <span className={HIGHLIGHTS.positive}>better hunting grounds</span>
        than the public side.
      </p>
      <p>
        Focus on maximizing duck populations and hunting as much as possible to
        achieve your objectives.
      </p>

      <p>On the map cells there are icons representing where the ducks were at the end of the previous year. In each cell is a series of pluses for each water management regime, which represents how many ducks will be in the cell if a particular regime is chosen <PopText recordName="townhall" text="at townhall" recordType="generic" />.</p>

      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Your estate has{" "}
        <span className={HIGHLIGHTS.positive}>
          naturally higher duck abundance
        </span>
        .
      </p>
    </div>
  ),
};

export default function RoleTutorial({ role }: { role: string }) {


  return (
    <div>
      {actionTutorialContent[role] ?? (
        <div>
          Sorry, no tutorial availible. Please read the tooltip for basic
          information.
        </div>
      )}
    </div>
  );
}
